<template>
	<div class="cog-dialog" tabindex="0">
		<i-ex />
		<el-dialog :title="title" :visible="visible" :close-on-click-modal="closeModal" v-on="$listeners" @opened="handleOpened" @closed="handleClosed" @mousedown.native="startDrag($event)" @mouseup.native="stopDrag($event)">
			<slot name="title" />
			<slot />
		</el-dialog>
	</div>
</template>

<script>
	import 'src/style/_motion.scss';

	import Vue from 'vue';
	import { Dialog } from '@cognitoforms/element-ui';
	import focusableEls from 'src/focusable-elements';
	import IEx from 'src/assets/ex.svg';

	Vue.use(Dialog);

	export default {
		name: 'CDialog',
		components: { IEx },
		inject: ['$resource'],
		props: {
			title: { type: String, default: '' },
			visible: Boolean
		},
		emits: ['opened'],
		data() {
			return {
				lastFocusedEl: null,
				startTarget: null,
				stopTarget: null,
				closeModal: false
			};
		},
		mounted() {
			const closeButton = this.$el.querySelector('.el-dialog__headerbtn');
			const ex = this.$el.querySelector('.cog-i-ex');

			if (ex)
				closeButton.appendChild(ex);

			closeButton.setAttribute('title', this.$resource('dialog-close'));

			this.$el.addEventListener('keydown', this.checkTabs);
		},
		destroyed() {
			this.$el.removeEventListener('keydown', this.checkTabs);
		},
		methods: {
			checkTabs(e) {
				// Keep focus inside of dialog when tabbing. Requirement 9143.
				if (e.which === 9) {
					const els = [...this.$el.querySelectorAll(focusableEls)];

					// Handle shift-tabbing when on first element of the dialog,
					// then handle forward tabbing when on last element
					if (e.shiftKey && els[0] === document.activeElement) {
						e.preventDefault();
						els[els.length - 1].focus();
					}
					else if (!e.shiftKey && els[els.length - 1] === document.activeElement) {
						e.preventDefault();
						els[0].focus();
					}
				}
			},
			handleOpened() {
				// Record what element had focus in order to return to that on close.
				this.lastFocusedEl = document.activeElement;

				// Put focus on the close button (or first button) of the modal
				// so you can't tab backwards outside of the modal.
				this.$el.querySelector('button').focus();
				this.$emit('opened', this.$el);
			},
			handleClosed() {
				if (this.lastFocusedEl)
					this.lastFocusedEl.focus();
			},
			// Bug 14327: Dialog closes if you click inside and drag out or click outside and drag in
			startDrag(e) {
				this.startTarget = e.target;
			},
			stopDrag(e) {
				this.stopTarget = e.target;

				// Checking to see if the first click matches the second click (ie if the user is dragging the mouse)
				if (this.startTarget !== this.stopTarget)
					this.closeModal = false;
				else
					this.closeModal = true;
			}
		}
	};
</script>

<style lang="scss">
#{$specificity-base} {

	.cog-dialog {

		.el-dialog {
			position: relative;
			width: 90%;
			max-width: 500px;
			border-radius: var(--border-radius);
			box-shadow: 0 0 6px var(--label__color, var(--color, black));
			box-sizing: border-box;
			margin: 0 auto 50px;
			animation: cog-zoom $speed-half;
			animation-fill-mode: forwards;

			@include popper-bg;

			@media (prefers-reduced-motion) {
				animation: none;
			}

			&.is-fullscreen {
				width: 100%;
				height: 100%;
				margin-top: 0;
				margin-bottom: 0;
				overflow: auto;
			}

			&__wrapper {
				position: fixed !important;
				bottom: 0;
				left: 0;
				right: 0;
				top: 0;
				margin: 0;
				overflow: auto;
				animation: cog-fade-in $speed-half;

				&::before {
					display: block;
					position: absolute;
					bottom: 0;
					left: 0;
					right: 0;
					top: 0;
					background-color: var(--label__color, var(--color, rgba(black, 70%)));
					opacity: .7;
					content: '';
				}
			}

			&__header {
				padding: $gutter-half;
			}

			&__headerbtn {
				position: absolute;
				right: $gutter-half;
				top: $gutter-half;
				z-index: $z-1;
				background: transparent;
				border: var(--input__border-width) var(--input__border-style) transparent;
				border-radius: var(--input__border-radius);
				padding: 0;

				&:focus {
					border-color: var(--highlight);
				}
			}

			&__body {
				padding: 0 calc(var(--gutter) * 1.5) calc(var(--gutter) * 1.5);
			}
		}

		.el-popup-parent--hidden {
			overflow: hidden;
		}

		.dialog-fade-leave-to {
			animation: cog-fade-out $speed-half;
		}
	}

	&.cog-iframe-embed {

		.el-dialog__wrapper::before {
			display: none;
		}

		.cog-form--light-background .el-dialog {
			box-shadow: 8px 8px 20px 8px hsla(var(--background-hsl), .5);
		}

		.cog-form--dark-background .el-dialog {
			box-shadow: 0 4px 12px 1px hsla(var(--background-hsl), .5);
		}
	}
}
</style>